import React, { useEffect, useMemo, useState } from "react";
import "./DefineFilterModal.css";

const FIELD_LABELS = {
  Country: "Pays",
  Price: "Prix",
  presence: "Présence",
  type: "Type",
  year: "Année"
};

const emptyFilters = {
  Country: [],
  Price: [],
  presence: [],
  type: [],
  year: [],
  attendingOnly: false
};

const toStringArray = values => {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .filter(value => value !== null && value !== undefined)
    .map(value => String(value));
};

const buildInitialFilters = initialFilters => {
  if (!initialFilters || typeof initialFilters !== "object") {
    return emptyFilters;
  }

  return {
    Country: toStringArray(initialFilters.Country),
    Price: toStringArray(initialFilters.Price),
    presence: toStringArray(initialFilters.presence),
    type: toStringArray(initialFilters.type),
    year: toStringArray(initialFilters.year),
    attendingOnly: Boolean(initialFilters.attendingOnly)
  };
};

const DefineFilterModal = ({
  open,
  options = emptyFilters,
  initialFilters,
  initialName = "",
  onClose,
  onSave,
  saving = false,
  errorMessage = ""
}) => {
  const normalizedOptions = useMemo(() => buildInitialFilters(options), [options]);
  const [selectedFilters, setSelectedFilters] = useState(buildInitialFilters(initialFilters));
  const [filterName, setFilterName] = useState(initialName);

  useEffect(() => {
    if (open) {
      setSelectedFilters(buildInitialFilters(initialFilters));
      setFilterName(initialName || "");
    }
  }, [open, initialFilters, initialName]);

  const handleToggle = (field, value) => {
    setSelectedFilters(prev => {
      const current = prev[field] || [];
      const exists = current.includes(value);
      const nextValues = exists ? current.filter(v => v !== value) : [...current, value];

      return {
        ...prev,
        [field]: nextValues
      };
    });
  };

  const handleSelectAll = field => {
    setSelectedFilters(prev => ({
      ...prev,
      [field]: normalizedOptions[field] || []
    }));
  };

  const handleClearField = field => {
    setSelectedFilters(prev => ({
      ...prev,
      [field]: []
    }));
  };

  const handleSave = event => {
    event.preventDefault();
    if (!onSave) {
      return;
    }

    onSave({
      name: filterName.trim(),
      filters: selectedFilters
    });
  };

  if (!open) {
    return null;
  }

  return (
    <div className="define-filter-modal__overlay" role="dialog" aria-modal="true">
      <div className="define-filter-modal__container">
        <div className="define-filter-modal__header">
          <h2>Définir mon filtre</h2>
          <button
            type="button"
            className="define-filter-modal__close"
            onClick={onClose}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        <form className="define-filter-modal__content" onSubmit={handleSave}>
          <label className="define-filter-modal__field">
            <span>Nom du filtre</span>
            <input
              type="text"
              value={filterName}
              onChange={event => setFilterName(event.target.value)}
              placeholder="Exemple : Conférences favorites"
              required
            />
          </label>

          <div className="define-filter-modal__filters">
            {Object.keys(FIELD_LABELS).map(field => {
              const values = normalizedOptions[field] || [];

              return (
                <div key={field} className="define-filter-modal__group">
                  <div className="define-filter-modal__group-header">
                    <h3>{FIELD_LABELS[field]}</h3>
                    <div className="define-filter-modal__group-actions">
                      <button type="button" onClick={() => handleSelectAll(field)}>
                        Tout sélectionner
                      </button>
                      <button type="button" onClick={() => handleClearField(field)}>
                        Effacer
                      </button>
                    </div>
                  </div>
                  <div className="define-filter-modal__checkbox-list">
                    {values.length === 0 ? (
                      <p className="define-filter-modal__empty">Aucune option disponible</p>
                    ) : (
                      values.map(rawValue => {
                        const value = String(rawValue);
                        const isChecked = selectedFilters[field]?.includes(value);

                        return (
                          <label key={value} className="define-filter-modal__checkbox">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggle(field, value)}
                            />
                            <span>{value || "Non renseigné"}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="define-filter-modal__single-toggle">
            <label className="define-filter-modal__checkbox define-filter-modal__checkbox--single">
              <input
                type="checkbox"
                checked={!!selectedFilters.attendingOnly}
                onChange={() => setSelectedFilters(prev => ({ ...prev, attendingOnly: !prev.attendingOnly }))}
              />
              <span>Afficher uniquement mes conferences a assister</span>
            </label>
          </div>

          {errorMessage && <div className="define-filter-modal__error">{errorMessage}</div>}

          <div className="define-filter-modal__footer">
            <button type="button" className="define-filter-modal__secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="define-filter-modal__primary" disabled={saving}>
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DefineFilterModal;
